import { Component, inject, OnInit, TemplateRef } from '@angular/core';
import { CustomersService } from "../../services/customers/customers.service";
import { CommonModule } from '@angular/common';
import { NzButtonModule } from "ng-zorro-antd/button";
import { NzModalModule, NzModalRef, NzModalService } from "ng-zorro-antd/modal";
import { FormsModule } from "@angular/forms";
import { NzFormModule } from "ng-zorro-antd/form";
import { NzSpinModule } from "ng-zorro-antd/spin";
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzSelectModule } from "ng-zorro-antd/select";
import { FeaturesService } from "../../services/features/features.service";
import { NzTableModule } from "ng-zorro-antd/table";
import { NzIconDirective } from "ng-zorro-antd/icon";
import { SearchInputComponent } from "../search-input/search-input.component";
import { NzTypographyModule } from 'ng-zorro-antd/typography';
import { NzInputModule } from 'ng-zorro-antd/input';
import { Router } from '@angular/router';
import { Buffer } from 'buffer';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { TenantUserStatisticsPipe } from '../../pipes/tenant-user-statistics.pipe';
import { SortByPipe } from "../../pipes/sort-by.pipe";
import { catchError, from, map, mergeMap, of, toArray } from 'rxjs';

type FilterGroup = 'stats' | 'email' | 'dev';

const FILTER_OPTIONS = {
  'stats-excluded': { group: 'stats', value: 'excluded' },
  'stats-included': { group: 'stats', value: 'included' },
  'email-set': { group: 'email', value: 'set' },
  'email-not-set': { group: 'email', value: 'not_set' },
  'dev-on': { group: 'dev', value: 'on' },
} as const satisfies Record<string, { group: FilterGroup; value: string }>;

type FilterKey = keyof typeof FILTER_OPTIONS;

@Component({
  selector: 'customers',
  standalone: true,
  imports: [CommonModule, FormsModule, NzButtonModule, NzFormModule, FormsModule, NzModalModule, NzSpinModule, NzSelectModule,
    NzSwitchModule, NzTableModule, NzIconDirective, SearchInputComponent, NzTypographyModule, NzInputModule, TenantUserStatisticsPipe, SortByPipe],
  templateUrl: './customers.component.html',
  styleUrl: './customers.component.scss'
})
export class CustomersComponent implements OnInit {
  customersService = inject(CustomersService);
  featuresService = inject(FeaturesService);
  notificationService = inject(NzNotificationService)
  router = inject(Router)
  listOfCustomers: any[] = [];
  filteredListOfCustomers: any[] = [];
  customer: any = {};
  customerEditId: string = "";
  modal = inject(NzModalService);
  isLoading = false;
  isModalLoading = false;

  searchValue = "";
  selectedFilters: FilterKey[] = [];
  devModeByCustomer: Map<string, boolean> | null = null;
  isDevModeLoading = false;
  loading = false;

  deleteTextValue = "";
  deleteText = "delete";


  ngOnInit(): void {
    this.getCustomers();
  }

  getCustomers() {
    this.isLoading = true
    this.customersService.getCustomers().subscribe({
      next: res => {
        this.listOfCustomers = res;
        this.devModeByCustomer = null;
        this.applyFilters();
        this.loadDevMode();
        this.isLoading = false
      },
      error: () => {
        this.isLoading = false;
        this.notificationService.error('Error', 'Please, Reload the page')
      }
    })

  }

  openCustomerDeleteModal(tplContent: TemplateRef<{}>, tplFooter: TemplateRef<{}>, customerDeleteId: string) {
    this.customer = this.listOfCustomers.find(customer => {
      return customer.pk === customerDeleteId;
    })
    this.deleteText = ("delete " + this.customer.company_name).trim()
    const modal: NzModalRef = this.modal.create({
      nzTitle: 'Delete tenant',
      nzContent: tplContent,
      nzFooter: tplFooter,
      nzOnCancel: () => this.handleModalClose()
    });
  }

  deleteCustomer(ref: any, customerId: string) {
    ref.destroy();
    this.isLoading = true
    this.customersService.deleteCustomer(customerId).subscribe(() => {
      this.isLoading = false
      this.customer = {}
      this.customerEditId = ""
      this.deleteTextValue = ""
      this.getCustomers();
    })
  }

  handleModalClose(ref?: any) {
    if (ref) {
      ref.destroy()
    }
    this.deleteTextValue = "";
    this.getCustomers();
  }

  handleSearchClick(value: string): void {
    this.searchValue = value;
    this.applyFilters();
  }

  onFiltersChange(keys: FilterKey[]): void {
    const byGroup = new Map<FilterGroup, FilterKey>();
    keys.forEach(key => byGroup.set(FILTER_OPTIONS[key].group, key));
    this.selectedFilters = [...byGroup.values()];
    this.applyFilters();
    this.loadDevMode();
  }

  private loadDevMode(): void {
    if (!this.filterValue('dev') || this.devModeByCustomer || this.isDevModeLoading) {
      return;
    }
    this.isDevModeLoading = true;
    from(this.listOfCustomers).pipe(
      mergeMap(customer => this.customersService.getCustomersFeatures(customer.pk).pipe(
        map((features: any[]) => [customer.pk, features.some(feature => feature.alias === 'dev-mode' && !feature.toggled_off)] as [string, boolean]),
        catchError(() => of([customer.pk, false] as [string, boolean]))
      ), 6),
      toArray()
    ).subscribe(entries => {
      this.devModeByCustomer = new Map(entries);
      this.isDevModeLoading = false;
      this.applyFilters();
    });
  }

  private filterValue(group: FilterGroup): string | undefined {
    const key = this.selectedFilters.find(key => FILTER_OPTIONS[key].group === group);
    return key && FILTER_OPTIONS[key].value;
  }

  applyFilters(): void {
    const search = this.searchValue.trim().toLowerCase();
    const statistics = this.filterValue('stats');
    const email = this.filterValue('email');
    const devMode = this.filterValue('dev');

    this.filteredListOfCustomers = this.listOfCustomers.filter(customer => {
      const matchesCompany =
        !search ||
        (customer.company_name || '').toLowerCase().includes(search);

      const matchesStatistics =
        !statistics ||
        customer.exclude_statistics === (statistics === 'excluded');

      const emailIsSet = !!customer.decision_maker_email?.trim();

      const matchesEmail =
        !email ||
        emailIsSet === (email === 'set');

      const matchesDevMode =
        !devMode ||
        !this.devModeByCustomer ||
        this.devModeByCustomer.get(customer.pk) === (devMode === 'on');
      return matchesCompany && matchesStatistics && matchesEmail && matchesDevMode;
    });
  }

  get hasActiveFilters(): boolean {
    return !!this.searchValue.trim() || this.selectedFilters.length > 0;
  }

  customerLink(data: any) {
    let id = Buffer.from(data.pk).toString('base64')
    this.router.navigateByUrl(`tenants/${id}`)
  }

  customerCreateLink() {
    this.router.navigateByUrl(`tenants/create`)
  }

  onPageIndexChange(): void {
    const antTable = document.querySelector('.ant-table');
    if (antTable) {
      antTable.scrollTop = 0;
    }
  }

}
